import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Page } from "@/components/Page";
import { EASE, Eyebrow, FadeUp } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TABS = ["all", "new", "in-progress", "closed"];

const StatusPill = ({ status }) => (
  <span
    data-testid={`status-pill-${status}`}
    className={`inline-block whitespace-nowrap border px-3 py-1 text-[9px] font-medium uppercase tracking-[0.24em] ${
      status === "new"
        ? "border-champagne/60 text-champagne"
        : status === "in-progress"
          ? "border-ivory/30 text-ivory"
          : "border-ivory-muted/25 text-ivory-muted/60"
    }`}
  >
    {status}
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = () =>
    api
      .get("/enquiries")
      .then((r) => setEnquiries(r.data))
      .catch(() => {});

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  const counts = useMemo(() => {
    const c = { all: enquiries.length, new: 0, "in-progress": 0, closed: 0 };
    enquiries.forEach((e) => {
      c[e.status] = (c[e.status] || 0) + 1;
    });
    return c;
  }, [enquiries]);

  const visible = tab === "all" ? enquiries : enquiries.filter((e) => e.status === tab);

  if (user === false) return <Navigate to="/login" replace />;
  if (user && user.role !== "admin") return <Navigate to="/account" replace />;

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  const sendReply = async (id) => {
    const message = (drafts[id] || "").trim();
    if (!message) return;
    setBusy(id);
    try {
      const { data } = await api.post(`/enquiries/${id}/replies`, { message });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      setDrafts((d) => ({ ...d, [id]: "" }));
      toast.success("Reply sent to the member's inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      const { data } = await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      toast.success(status === "closed" ? "Enquiry closed." : "Enquiry reopened.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <Page testId="admin-page">
      <section className="mx-auto min-h-screen max-w-[1100px] px-6 pb-28 pt-40 lg:pt-48">
        <FadeUp>
          <Eyebrow>Concierge Desk</Eyebrow>
        </FadeUp>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <FadeUp delay={0.1}>
            <h1 className="font-serif text-4xl font-medium text-ivory sm:text-5xl">
              Member <span className="gold-text italic">enquiries.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <LuxButton variant="outline" onClick={signOut} testId="admin-logout-button">
              Sign Out
            </LuxButton>
          </FadeUp>
        </div>

        <FadeUp delay={0.25} className="mt-12">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter enquiries">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`border px-5 py-2.5 text-[10px] font-medium uppercase tracking-[0.24em] transition-all duration-300 ${
                  tab === t
                    ? "border-champagne bg-champagne/10 text-champagne"
                    : "border-[rgba(200,164,93,0.2)] text-ivory-muted hover:border-champagne/50 hover:text-ivory"
                }`}
              >
                {t === "in-progress" ? "In progress" : t} · {counts[t] || 0}
              </button>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.35} className="mt-8">
          <div className="border-t border-[rgba(200,164,93,0.16)]" data-testid="admin-enquiries-list">
            {visible.length === 0 && (
              <p className="py-12 text-[13px] font-light text-ivory-muted" data-testid="admin-empty-state">
                No enquiries under this view.
              </p>
            )}
            {visible.map((e) => {
              const open = openId === e.id;
              return (
                <div key={e.id} className="border-b border-[rgba(200,164,93,0.12)]" data-testid={`admin-enquiry-${e.id}`}>
                  <button
                    type="button"
                    data-testid={`admin-enquiry-toggle-${e.id}`}
                    onClick={() => setOpenId(open ? null : e.id)}
                    aria-expanded={open}
                    className="grid w-full grid-cols-[1fr_auto] items-center gap-3 py-6 text-left transition-colors duration-300 hover:bg-white/[0.015] sm:grid-cols-[130px_1fr_140px_110px_24px] sm:gap-6"
                  >
                    <span className="text-[10px] font-medium uppercase tracking-[0.26em] text-champagne">
                      {e.service}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-serif text-lg font-medium text-ivory">
                        {e.first_name} {e.last_name}
                      </span>
                      <span className="block truncate text-[12px] font-light text-ivory-muted">
                        {e.message}
                      </span>
                    </span>
                    <span className="hidden text-[11px] font-light tracking-wide text-ivory-muted/70 sm:block">
                      {fmtDate(e.created_at)}
                    </span>
                    <span className="hidden sm:block">
                      <StatusPill status={e.status} />
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 justify-self-end text-champagne/70 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                      strokeWidth={1.5}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-10 pb-9 pt-1 sm:grid-cols-[1.2fr_1fr]">
                          <div>
                            <p className="lux-label">Enquiry</p>
                            <p className="mt-3 text-sm font-light leading-relaxed text-ivory" data-testid={`admin-enquiry-message-${e.id}`}>
                              {e.message}
                            </p>
                            <div className="mt-6 space-y-1.5 text-[12px] font-light text-ivory-muted">
                              <p data-testid={`admin-enquiry-email-${e.id}`}>{e.email}</p>
                              {e.phone && <p>{e.phone}</p>}
                              {e.country && <p>{e.country}</p>}
                            </div>
                            {e.replies?.length > 0 && (
                              <div className="mt-7 border-t border-[rgba(200,164,93,0.14)] pt-6">
                                <p className="lux-label">Replies</p>
                                <div className="mt-4 space-y-4" data-testid={`admin-replies-${e.id}`}>
                                  {e.replies.map((r, i) => (
                                    <div key={i} className="border-l border-champagne/40 pl-4">
                                      <p className="text-sm font-light leading-relaxed text-ivory">{r.message}</p>
                                      <p className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-ivory-muted/60">
                                        {r.author} · {fmtDate(r.created_at)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div>
                            <label htmlFor={`reply-${e.id}`} className="lux-label">
                              Reply to member
                            </label>
                            <textarea
                              id={`reply-${e.id}`}
                              data-testid={`admin-reply-textarea-${e.id}`}
                              rows={4}
                              value={drafts[e.id] || ""}
                              onChange={(ev) => setDrafts((d) => ({ ...d, [e.id]: ev.target.value }))}
                              className="lux-input resize-none"
                              placeholder="Compose a considered response…"
                            />
                            <div className="mt-6 flex flex-wrap gap-3">
                              <LuxButton
                                variant="gold"
                                onClick={() => sendReply(e.id)}
                                disabled={busy === e.id || !(drafts[e.id] || "").trim()}
                                testId={`admin-send-reply-${e.id}`}
                                className="px-6 py-3"
                              >
                                Send Reply
                              </LuxButton>
                              {e.status !== "closed" ? (
                                <LuxButton
                                  variant="outline"
                                  onClick={() => setStatus(e.id, "closed")}
                                  disabled={busy === e.id}
                                  testId={`admin-close-enquiry-${e.id}`}
                                  className="px-6 py-3"
                                >
                                  Close Enquiry
                                </LuxButton>
                              ) : (
                                <LuxButton
                                  variant="outline"
                                  onClick={() => setStatus(e.id, "in-progress")}
                                  disabled={busy === e.id}
                                  testId={`admin-reopen-enquiry-${e.id}`}
                                  className="px-6 py-3"
                                >
                                  Reopen
                                </LuxButton>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </FadeUp>
      </section>
    </Page>
  );
}
